/**
 * TypePopup — eerste-laag popup voor een MateriaalType.
 * Hero-afbeelding (bewerkbaar), toegevoegd op, waarde + omschrijving (inline),
 * bewerkbare sets-lijst, en exemplaren met toevoegen/verwijderen.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box, Typography, Dialog, DialogContent, TextField, IconButton, Chip,
  Autocomplete, CircularProgress, Tooltip, Menu, MenuItem, useMediaQuery, useTheme,
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import { BACKEND_URL } from '../../utils/apiConfig';
import IconColorPicker, { MaterialVisual } from './IconColorPicker';

const T = {
  primary: '#526075', primaryDim: '#465469', primaryContainer: '#d5e3fd',
  tertiary: '#635983', tertiaryContainer: '#dbcdff',
  surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  emerald: '#10b981', emeraldBg: '#ecfdf5', error: '#a83836', errorBg: '#fef2f2',
  amber: '#f59e0b', font: '"Manrope", system-ui, sans-serif', fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", lineHeight: 1, display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle', ...sx }}>{name}</span>
);

const CATEGORY_ICONS = {
  'Cameras': 'videocam', 'Drones': 'flight', 'Lens': 'camera', 'Monitors': 'monitor',
  'Statief': 'straighten', 'Geluid': 'mic', 'Grip': 'handyman', 'Licht': 'lightbulb',
  'Camera Accessoires': 'settings_input_component', 'Special effects': 'auto_awesome',
  'Studio equipment': 'storefront', 'Vervoer en karren': 'local_shipping', 'Kabels': 'cable',
};
const STATUS_COLORS = {
  'In gebruik': T.emerald, 'Beschikbaar': T.emerald,
  'Reparatie, wel bruikbaar': T.amber, 'Reparatie, niet bruikbaar': T.error, 'Reparatie': T.error,
  'Lange tijd afwezig': T.outline, 'Marktplaats': T.tertiary, 'Verkocht/gearchiveerd': T.outlineVariant,
};
const STATUS_OPTIONS = ['Reparatie, wel bruikbaar', 'Reparatie, niet bruikbaar', 'Lange tijd afwezig', 'Marktplaats', 'Verkocht/gearchiveerd'];
const STATUS_LABEL = (s) => s || 'Geen status';

const FieldLabel = ({ children }) => (
  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 700, color: T.outlineVariant, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>{children}</Typography>
);
const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: '0.55rem', fontFamily: T.fontBody, fontSize: '0.85rem', bgcolor: T.surface } };

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

export default function TypePopup({ open, group, allSets = [], onClose, onChanged, onExemplaarClick, getMaterialReservations }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const fileRef = useRef(null);

  const tf = group?.typeFields || {};
  const typeId = group?.type?.id;
  const isOrphan = !!group?.isOrphan;
  const children = group?.children || [];
  const categorie = group?.categorie || tf.Categorie;
  const isVehicle = categorie === 'Vervoer en karren';

  const [form, setForm] = useState({ Waarde: '', Omschrijving: '', icon: null, color: null });
  const [selectedSets, setSelectedSets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [addingExemplaar, setAddingExemplaar] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [exSort, setExSort] = useState('nummer'); // 'nummer' | 'alfabetisch'
  const [archiveAsk, setArchiveAsk] = useState(false);
  const [statusAnchor, setStatusAnchor] = useState(null);
  const [statusMat, setStatusMat] = useState(null);
  const [showArchivedEx, setShowArchivedEx] = useState(false);
  const [photoAnchor, setPhotoAnchor] = useState(false);

  // Gearchiveerde sets niet kiesbaar bij set-toewijzing
  const setOptions = useMemo(() => allSets.filter(s => !s.fields?.Gearchiveerd).map(s => ({ id: s.id, label: s.fields?.ProjectType || s.fields?.Omschrijving || 'Set' })), [allSets]);
  const setsContaining = useMemo(
    () => allSets.filter(s => (s.fields?.MaterialenType || []).includes(group?.type?.id)),
    [allSets, group]
  );

  const sortedChildren = useMemo(() => {
    const arr = [...children];
    if (exSort === 'alfabetisch') {
      arr.sort((a, b) => (a.fields?.ID || '').localeCompare(b.fields?.ID || ''));
    } else {
      const num = (m) => { const n = parseInt(m.fields?.SetNr, 10); return Number.isNaN(n) ? Infinity : n; };
      arr.sort((a, b) => num(a) - num(b) || (a.fields?.SetNr || '').localeCompare(b.fields?.SetNr || ''));
    }
    return arr;
  }, [children, exSort]);

  useEffect(() => {
    if (group) {
      setForm({ Naam: tf.MateriaalType || '', Waarde: tf.Waarde || '', Omschrijving: tf.Omschrijving || tf.Opmerkingen || '', icon: tf.Icoon || null, color: tf.Kleur || null, noReservation: !!tf.GeenReserveringNodig,
        Brandstoftype: tf.Brandstoftype || '', WLTP_L: tf.WLTP_L_per_100km ?? '', WLTP_Wh: tf.WLTP_Wh_per_km ?? '',
        TrackBrandstof: tf.TrackBrandstof !== false });
      const setIds = tf.TypeDraaidagSets || [];
      setSelectedSets(setOptions.filter(o => setIds.includes(o.id)));
      setConfirmDelete(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, allSets]);

  if (!group) return null;

  const imageUrl = tf.Afbeelding || null;

  const archived = !!tf.Gearchiveerd && !isOrphan;

  const handleSaveType = async () => {
    if (isOrphan || !typeId) { onClose(); return; }
    setSaving(true);
    try {
      await axios.patch(`${BACKEND_URL}/Api/materiaaltypes/${typeId}`, { fields: {
        MateriaalType: (form.Naam || '').trim() || tf.MateriaalType,
        Waarde: form.Waarde, Omschrijving: form.Omschrijving,
        TypeDraaidagSets: selectedSets.map(s => s.id),
        Icoon: form.icon, Kleur: form.color,
        GeenReserveringNodig: form.noReservation,
        ...(isVehicle ? {
          Brandstoftype: form.Brandstoftype || null,
          WLTP_L_per_100km: form.WLTP_L === '' ? null : Number(form.WLTP_L),
          WLTP_Wh_per_km: form.WLTP_Wh === '' ? null : Number(form.WLTP_Wh),
          TrackBrandstof: form.TrackBrandstof !== false,
        } : {}),
      } });
      toast.success('Materiaal opgeslagen');
      onChanged && onChanged();
      onClose();
    } catch (err) {
      toast.error('Opslaan mislukt: ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); }
  };

  const handleArchiveToggle = async () => {
    if (isOrphan || !typeId) return;
    setSaving(true);
    try {
      await axios.patch(`${BACKEND_URL}/Api/materiaaltypes/${typeId}`, { fields: { Gearchiveerd: !archived } });
      toast.success(!archived ? 'Materiaal gearchiveerd' : 'Materiaal teruggezet');
      onChanged && onChanged();
      onClose();
    } catch (err) {
      toast.error('Mislukt: ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); }
  };

  const handleImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !typeId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${BACKEND_URL}/Api/materiaaltypes/${typeId}/afbeelding`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Afbeelding geüpload');
      onChanged && onChanged();
    } catch (err) {
      toast.error('Upload mislukt: ' + (err.response?.data?.error || err.message));
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleDeleteImage = async () => {
    if (!typeId) return;
    if (!(await bevestig({ titel: 'Afbeelding verwijderen?', tekst: 'De afbeelding van dit type wordt definitief verwijderd.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    setUploading(true);
    try {
      await axios.delete(`${BACKEND_URL}/Api/materiaaltypes/${typeId}/afbeelding`);
      toast.success('Afbeelding verwijderd');
      onChanged && onChanged();
    } catch (err) {
      toast.error('Verwijderen mislukt: ' + (err.response?.data?.error || err.message));
    } finally { setUploading(false); }
  };

  const handleDownloadImage = async () => {
    if (!imageUrl) return;
    try {
      const res = await fetch(imageUrl);
      const blob = await res.blob();
      const ext = (blob.type && blob.type.split('/')[1]) || 'jpg';
      const safeName = (tf.MateriaalType || 'materiaal').replace(/[^a-z0-9_-]+/gi, '_');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeName}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Downloaden mislukt: ' + err.message);
    }
  };

  const handleAddExemplaar = async () => {
    if (!typeId) return;
    setAddingExemplaar(true);
    try {
      await axios.post(`${BACKEND_URL}/Api/materialen`, { materiaalTypeId: typeId });
      if (onChanged) await onChanged();
      toast.success('Exemplaar toegevoegd');
    } catch (err) {
      toast.error('Toevoegen mislukt: ' + (err.response?.data?.error || err.message));
    } finally { setAddingExemplaar(false); }
  };

  const setExemplaarStatus = async (newStatus) => {
    const mat = statusMat;
    setStatusAnchor(null); setStatusMat(null);
    if (!mat) return;
    try {
      await axios.patch(`${BACKEND_URL}/Api/materialen/${mat.id}`, { fields: { Status: newStatus } });
      if (onChanged) await onChanged();
      toast.success('Status bijgewerkt');
    } catch (err) {
      toast.error('Mislukt: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleArchiveExemplaar = async (mat) => {
    setConfirmDelete(null);
    try {
      await axios.patch(`${BACKEND_URL}/Api/materialen/${mat.id}`, { fields: { Status: 'Verkocht/gearchiveerd' } });
      if (onChanged) await onChanged();
      toast.success('Exemplaar gearchiveerd');
    } catch (err) {
      toast.error('Archiveren mislukt: ' + (err.response?.data?.error || err.message));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : '1.1rem', display: 'flex', flexDirection: 'column', maxHeight: fullScreen ? '100%' : '92vh' } }}>
      {/* Hero */}
      <Box sx={{ position: 'relative', height: { xs: 200, sm: 280 }, bgcolor: T.surfaceLow, flexShrink: 0, overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {imageUrl ? (
            // Foto past op hoogte (smaller, gecentreerd) i.p.v. de volle breedte croppen
            <Box component="img" src={imageUrl} alt="" loading="lazy" sx={{ height: '100%', width: 'auto', maxWidth: '100%', objectFit: 'contain', display: 'block' }} />
          ) : (
            <MaterialVisual icon={tf.Icoon} color={tf.Kleur} fallbackIcon={CATEGORY_ICONS[categorie] || 'inventory_2'} radius={0} iconSize={88} />
          )}
        </Box>
        {/* gradient + title overlay */}
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(20,24,28,0.78) 0%, rgba(20,24,28,0.05) 45%, rgba(20,24,28,0) 100%)' }} />
        <Box sx={{ position: 'absolute', left: 20, right: 64, bottom: 14 }}>
          {categorie && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.85, py: 0.25, borderRadius: '0.4rem', bgcolor: 'rgba(255,255,255,0.9)', mb: 0.75 }}>
              <Icon name={CATEGORY_ICONS[categorie] || 'category'} size={13} sx={{ color: T.primary }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 700, color: T.primary }}>{categorie}</Typography>
            </Box>
          )}
          <Typography sx={{ fontFamily: T.font, fontSize: { xs: '1.3rem', sm: '1.6rem' }, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', lineHeight: 1.1, textShadow: '0 1px 8px rgba(0,0,0,0.4)' }}>
            {tf.MateriaalType || 'Onbekend type'}
          </Typography>
        </Box>
        {/* sluiten — rechtsboven */}
        <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
          <IconButton size="small" onClick={onClose} sx={{ bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: '#fff' } }}><Icon name="close" size={18} sx={{ color: T.text }} /></IconButton>
        </Box>
        {/* foto-acties — gegroepeerd rechtsonder */}
        {!isOrphan && (
          <Box sx={{ position: 'absolute', bottom: 14, right: 16 }}>
            <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp,.gif,.svg" hidden onChange={handleImageFile} />
            <Tooltip title="Foto-opties" arrow>
              <IconButton
                size="small"
                onClick={(e) => setPhotoAnchor(e.currentTarget)}
                disabled={uploading}
                sx={{ bgcolor: 'rgba(255,255,255,0.92)', '&:hover': { bgcolor: '#fff' }, boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}
              >
                {uploading ? <CircularProgress size={16} sx={{ color: T.primary }} /> : <Icon name="photo_camera" size={18} sx={{ color: T.text }} />}
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={photoAnchor}
              open={!!photoAnchor}
              onClose={() => setPhotoAnchor(false)}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
              <MenuItem onClick={() => { setPhotoAnchor(false); fileRef.current?.click(); }} sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', gap: 1 }}>
                <Icon name="photo_camera" size={17} sx={{ color: T.text }} />
                {imageUrl ? 'Foto vervangen' : 'Nieuwe foto'}
              </MenuItem>
              {imageUrl && (
                <MenuItem onClick={() => { setPhotoAnchor(false); handleDownloadImage(); }} sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', gap: 1 }}>
                  <Icon name="download" size={17} sx={{ color: T.text }} />
                  Foto downloaden
                </MenuItem>
              )}
              {imageUrl && (
                <MenuItem onClick={() => { setPhotoAnchor(false); handleDeleteImage(); }} sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', gap: 1, color: T.error }}>
                  <Icon name="hide_image" size={17} sx={{ color: T.error }} />
                  Foto verwijderen
                </MenuItem>
              )}
            </Menu>
          </Box>
        )}
      </Box>

      <DialogContent sx={{ flex: 1, overflow: 'auto', px: { xs: 2.5, sm: 3 }, py: 2.5 }}>
        {isOrphan && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, mb: 2, borderRadius: '0.6rem', bgcolor: T.surfaceLow }}>
            <Icon name="info" size={16} sx={{ color: T.outline }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.secondary }}>Dit exemplaar is niet aan een type gekoppeld. Type-gegevens zijn niet bewerkbaar.</Typography>
          </Box>
        )}

        {/* Naam (bewerkbaar) */}
        {!isOrphan && (
          <Box sx={{ mb: 2 }}>
            <FieldLabel>Naam</FieldLabel>
            <TextField size="small" fullWidth value={form.Naam} onChange={(e) => setForm(p => ({ ...p, Naam: e.target.value }))} sx={inputSx} />
          </Box>
        )}

        {/* Meta */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
          <Box>
            <FieldLabel>Toegevoegd op</FieldLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: 40 }}>
              <Icon name="event" size={15} sx={{ color: T.outline }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.text }}>{fmtDate(tf.ToegevoegdOp)}</Typography>
            </Box>
          </Box>
          <Box>
            <FieldLabel>Waarde</FieldLabel>
            <TextField size="small" fullWidth value={form.Waarde} disabled={isOrphan} onChange={(e) => setForm(p => ({ ...p, Waarde: e.target.value }))} placeholder="bv. € 4.500" sx={inputSx} />
          </Box>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <FieldLabel>Omschrijving</FieldLabel>
          <TextField size="small" fullWidth multiline minRows={2} value={form.Omschrijving} disabled={isOrphan} onChange={(e) => setForm(p => ({ ...p, Omschrijving: e.target.value }))} sx={inputSx} />
        </Box>

        {/* Voertuig-attributen (type-niveau): tracking, brandstoftype + WLTP-verbruik */}
        {isVehicle && !isOrphan && (
          <Box sx={{ mb: 2.5, p: 1.5, borderRadius: '0.7rem', bgcolor: T.surfaceLow, border: '1px solid rgba(45,51,53,0.06)' }}>
            <FieldLabel>Voertuiggegevens</FieldLabel>

            {/* Track brandstofverbruik */}
            <Box onClick={() => setForm(p => ({ ...p, TrackBrandstof: !(p.TrackBrandstof !== false) }))}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.8, cursor: 'pointer', mb: 1.5, userSelect: 'none' }}>
              <Icon name={form.TrackBrandstof !== false ? 'check_box' : 'check_box_outline_blank'} size={20}
                sx={{ color: form.TrackBrandstof !== false ? T.emerald : T.outlineVariant }} />
              <Box>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 700, color: T.text }}>Brandstofverbruik volgen</Typography>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', color: T.outline }}>Uit staat = grijs op de voertuigen-pagina en niet in de kilometer-invoer.</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <TextField select size="small" label="Brandstoftype" value={form.Brandstoftype || ''} onChange={(e) => setForm(p => ({ ...p, Brandstoftype: e.target.value }))} sx={{ ...inputSx, flex: 1, minWidth: 130 }} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}>
                <option value="">—</option>
                {['Diesel', 'Benzine', 'Elektrisch', 'Hybride', 'LPG'].map(f => <option key={f} value={f}>{f}</option>)}
              </TextField>
              <TextField size="small" type="number" label="WLTP (L / 100 km)" helperText="Voor brandstofmotoren" value={form.WLTP_L ?? ''} onChange={(e) => setForm(p => ({ ...p, WLTP_L: e.target.value }))} sx={{ ...inputSx, flex: 1, minWidth: 140 }} FormHelperTextProps={{ sx: { fontFamily: T.fontBody, fontSize: '0.58rem', mx: 0.2 } }} />
              <TextField size="small" type="number" label="WLTP (Wh / km)" helperText="Voor elektrisch / hybride" value={form.WLTP_Wh ?? ''} onChange={(e) => setForm(p => ({ ...p, WLTP_Wh: e.target.value }))} sx={{ ...inputSx, flex: 1, minWidth: 140 }} FormHelperTextProps={{ sx: { fontFamily: T.fontBody, fontSize: '0.58rem', mx: 0.2 } }} />
            </Box>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', color: T.outline, mt: 0.8 }}>
              Geldt voor alle exemplaren van dit voertuig. Kenteken en tracker-IMEI staan per exemplaar.
            </Typography>
          </Box>
        )}

        {/* Icoon + kleur (ook naast een foto) */}
        {!isOrphan && (
          <Box sx={{ mb: 2.5 }}>
            <FieldLabel>Icoon &amp; kleur</FieldLabel>
            <IconColorPicker icon={form.icon} color={form.color} onChange={({ icon, color }) => setForm(p => ({ ...p, icon, color }))} label="Icoon & kleur" />
          </Box>
        )}

        {/* Reservering nodig / Geen reservering nodig */}
        {!isOrphan && (
          <Box sx={{ mb: 2.5 }}>
            <FieldLabel>Reserveren</FieldLabel>
            <Box sx={{ display: 'inline-flex', borderRadius: '0.5rem', overflow: 'hidden', border: `1px solid ${T.surfaceHigh}` }}>
              {[{ v: false, l: 'Reservering nodig' }, { v: true, l: 'Geen reservering nodig' }].map(o => (
                <Box key={String(o.v)} onClick={() => setForm(p => ({ ...p, noReservation: o.v }))} sx={{ px: 1.5, py: 0.6, cursor: 'pointer', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.72rem', bgcolor: form.noReservation === o.v ? T.primary : T.surface, color: form.noReservation === o.v ? '#fff' : T.secondary }}>{o.l}</Box>
              ))}
            </Box>
            {form.noReservation && (
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.66rem', color: T.outline, mt: 0.5 }}>Exemplaren kunnen onbeperkt gereserveerd worden — geen conflicten (genoeg in magazijn).</Typography>
            )}
          </Box>
        )}

        {/* Sets */}
        {!isOrphan && (
          <Box sx={{ mb: 2.5 }}>
            <FieldLabel>Sets waarin dit type zit</FieldLabel>
            <Autocomplete
              multiple size="small" options={setOptions} value={selectedSets}
              onChange={(_, v) => setSelectedSets(v)}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderTags={(value, getTagProps) => value.map((opt, index) => (
                <Chip {...getTagProps({ index })} key={opt.id} label={opt.label} size="small"
                  icon={<span className="material-symbols-outlined" style={{ fontSize: 14, marginLeft: 6, color: T.tertiary }}>backpack</span>}
                  sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 600, bgcolor: T.tertiaryContainer, color: T.tertiary }} />
              ))}
              renderInput={(params) => <TextField {...params} placeholder="Set toevoegen…" sx={inputSx} />}
            />
          </Box>
        )}

        {/* Exemplaren */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontFamily: T.font, fontSize: '0.85rem', fontWeight: 800, color: T.text }}>Exemplaren ({children.length})</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {children.length > 1 && (
              <Box sx={{ display: 'inline-flex', borderRadius: '0.45rem', overflow: 'hidden', border: `1px solid ${T.surfaceHigh}` }}>
                {[{ k: 'nummer', l: 'Nr' }, { k: 'alfabetisch', l: 'A-Z' }].map(o => (
                  <Box key={o.k} onClick={() => setExSort(o.k)} sx={{ px: 1, py: 0.4, cursor: 'pointer', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.66rem', bgcolor: exSort === o.k ? T.primary : T.surface, color: exSort === o.k ? '#fff' : T.secondary }}>{o.l}</Box>
                ))}
              </Box>
            )}
            {!isOrphan && (
              <Box onClick={() => !addingExemplaar && handleAddExemplaar()} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.5, borderRadius: '0.5rem', cursor: 'pointer', bgcolor: T.primaryContainer, color: T.primary, fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.72rem', '&:hover': { filter: 'brightness(0.97)' } }}>
                {addingExemplaar ? <CircularProgress size={13} sx={{ color: T.primary }} /> : <Icon name="add" size={16} sx={{ color: T.primary }} />}
                Exemplaar
              </Box>
            )}
          </Box>
        </Box>
        {(() => {
          const renderRow = (mat) => {
            const mf = mat.fields || {};
            const status = mf.Status || '';
            const dot = STATUS_COLORS[status] || T.outlineVariant;
            const isArchivedEx = status === 'Verkocht/gearchiveerd';
            const resCount = (getMaterialReservations ? getMaterialReservations(mat.id) : []).length;
            const isConfirming = confirmDelete === mat.id;
            return (
              <Box key={mat.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: '0.55rem', bgcolor: T.surfaceLow, border: `1px solid ${T.surfaceHigh}`, opacity: isArchivedEx ? 0.55 : 1, transition: 'all 0.12s', '&:hover': { borderColor: `${T.primary}30` } }}>
                <Box onClick={() => onExemplaarClick(mat)} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1, minWidth: 0, cursor: 'pointer' }}>
                  <Tooltip title={STATUS_LABEL(status)} arrow><Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: dot, flexShrink: 0 }} /></Tooltip>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mf.ID || `Exemplaar ${mf.SetNr || ''}`}</Typography>
                    {mf.Omschrijving && (
                      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.66rem', color: T.secondary, mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mf.Omschrijving}</Typography>
                    )}
                  </Box>
                  {resCount > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      <Icon name="event" size={12} sx={{ color: T.amber }} />
                      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 700, color: T.amber }}>{resCount}</Typography>
                    </Box>
                  )}
                </Box>
                {/* Inline status (klikbaar) */}
                {!isOrphan && (
                  <Tooltip title="Status aanpassen" arrow>
                    <Box onClick={(e) => { e.stopPropagation(); setStatusMat(mat); setStatusAnchor(e.currentTarget); }} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35, px: 0.6, py: 0.3, borderRadius: '0.35rem', cursor: 'pointer', border: `1px solid ${T.surfaceHigh}`, bgcolor: T.surface, flexShrink: 0, maxWidth: 150, '&:hover': { borderColor: T.outlineVariant } }}>
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: dot, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 700, color: T.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{status || 'Status'}</Typography>
                      <Icon name="expand_more" size={13} sx={{ color: T.outlineVariant }} />
                    </Box>
                  </Tooltip>
                )}
                {!isOrphan && !isArchivedEx && (
                  isConfirming ? (
                    <Box sx={{ display: 'inline-flex', gap: 0.25 }}>
                      <Tooltip title="Archiveren bevestigen" arrow><IconButton size="small" onClick={() => handleArchiveExemplaar(mat)} sx={{ bgcolor: T.amber, '&:hover': { filter: 'brightness(0.92)' } }}><Icon name="check" size={15} sx={{ color: '#fff' }} /></IconButton></Tooltip>
                      <Tooltip title="Annuleren" arrow><IconButton size="small" onClick={() => setConfirmDelete(null)}><Icon name="close" size={15} sx={{ color: T.outline }} /></IconButton></Tooltip>
                    </Box>
                  ) : (
                    <Tooltip title="Archiveren (niet meer reserveerbaar)" arrow><IconButton size="small" onClick={() => setConfirmDelete(mat.id)}><Icon name="archive" size={16} sx={{ color: T.outline }} /></IconButton></Tooltip>
                  )
                )}
              </Box>
            );
          };
          const activeEx = sortedChildren.filter(m => m.fields?.Status !== 'Verkocht/gearchiveerd');
          const archivedEx = sortedChildren.filter(m => m.fields?.Status === 'Verkocht/gearchiveerd');
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {activeEx.map(renderRow)}
              {children.length === 0 && (
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.outlineVariant, fontStyle: 'italic', py: 1 }}>Nog geen exemplaren</Typography>
              )}
              {archivedEx.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                  <Box onClick={() => setShowArchivedEx(v => !v)} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.75, cursor: 'pointer', userSelect: 'none' }}>
                    <Icon name={showArchivedEx ? 'expand_more' : 'chevron_right'} size={18} sx={{ color: T.outline }} />
                    <Icon name="archive" size={14} sx={{ color: T.outline }} />
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700, color: T.secondary }}>Gearchiveerd ({archivedEx.length})</Typography>
                  </Box>
                  {showArchivedEx && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>{archivedEx.map(renderRow)}</Box>}
                </Box>
              )}
            </Box>
          );
        })()}
      </DialogContent>

      {/* Footer */}
      {!isOrphan && (
        <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${T.surfaceHigh}`, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={archived ? 'Terugzetten uit archief' : 'Archiveren (exemplaren niet meer reserveerbaar)'} arrow>
            <Box onClick={() => { if (saving) return; if (!archived && setsContaining.length > 0) setArchiveAsk(true); else handleArchiveToggle(); }} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.7, borderRadius: '0.5rem', cursor: 'pointer', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.78rem', color: archived ? T.primary : T.secondary, border: `1px solid ${T.surfaceHigh}`, '&:hover': { bgcolor: T.surfaceLow } }}>
              <Icon name={archived ? 'unarchive' : 'archive'} size={16} /> {archived ? 'Terugzetten' : 'Archiveren'}
            </Box>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          <Box onClick={onClose} sx={{ px: 2, py: 0.7, borderRadius: '0.5rem', cursor: 'pointer', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.8rem', color: T.secondary, '&:hover': { bgcolor: T.surfaceLow } }}>Annuleren</Box>
          <Box onClick={() => !saving && handleSaveType()} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 2.5, py: 0.7, borderRadius: '0.5rem', cursor: saving ? 'default' : 'pointer', background: `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)`, color: '#fff', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.8rem', boxShadow: '0 4px 12px rgba(99,89,131,0.25)', opacity: saving ? 0.7 : 1, '&:hover': { boxShadow: '0 6px 20px rgba(99,89,131,0.35)' } }}>
            {saving && <CircularProgress size={14} sx={{ color: '#fff' }} />}
            Opslaan
          </Box>
        </Box>
      )}

      {/* Inline status-menu voor exemplaren */}
      <Menu anchorEl={statusAnchor} open={!!statusAnchor} onClose={() => { setStatusAnchor(null); setStatusMat(null); }}>
        {STATUS_OPTIONS.map(s => (
          <MenuItem key={s} onClick={() => setExemplaarStatus(s)} sx={{ fontFamily: T.fontBody, fontSize: '0.82rem' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STATUS_COLORS[s] || T.outline, mr: 1 }} />{s}
          </MenuItem>
        ))}
        <MenuItem onClick={() => setExemplaarStatus('')} sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.outline }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', border: `1px solid ${T.outlineVariant}`, mr: 1 }} />Geen status
        </MenuItem>
      </Menu>

      {/* Archief-bevestiging: toon in welke sets dit materiaal zit */}
      <Dialog open={archiveAsk} onClose={() => setArchiveAsk(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '1rem' } }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Icon name="archive" size={22} sx={{ color: T.amber }} />
            <Typography sx={{ fontFamily: T.font, fontSize: '1.05rem', fontWeight: 800, color: T.text }}>Materiaal archiveren?</Typography>
          </Box>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.secondary, mb: 1.5 }}>
            Dit materiaal zit in {setsContaining.length} set{setsContaining.length === 1 ? '' : 's'}. Bij archiveren wordt het <strong>uit deze set{setsContaining.length === 1 ? '' : 's'} verwijderd</strong> en zijn de exemplaren niet meer reserveerbaar:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            {setsContaining.map(s => (
              <Chip key={s.id} label={s.fields?.ProjectType || 'Set'} size="small"
                icon={<span className="material-symbols-outlined" style={{ fontSize: 13, marginLeft: 6, color: T.tertiary }}>backpack</span>}
                sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 600, bgcolor: T.tertiaryContainer, color: T.tertiary }} />
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Box onClick={() => setArchiveAsk(false)} sx={{ px: 2, py: 0.7, borderRadius: '0.5rem', cursor: 'pointer', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.8rem', color: T.secondary, '&:hover': { bgcolor: T.surfaceLow } }}>Annuleren</Box>
            <Box onClick={() => { setArchiveAsk(false); handleArchiveToggle(); }} sx={{ px: 2.5, py: 0.7, borderRadius: '0.5rem', cursor: 'pointer', bgcolor: T.amber, color: '#fff', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.8rem', '&:hover': { filter: 'brightness(0.95)' } }}>Archiveren</Box>
          </Box>
        </Box>
      </Dialog>
    </Dialog>
  );
}
